import React, { useMemo, useState } from 'react';
import type { Caption } from '@remotion/captions';
import { groupIntoLines } from '../lib/srt';
import { formatTimecode } from '../lib/pipeline';

/**
 * Whisper misrecognises words — proper nouns, jargon, accented speech — and
 * these captions get burned permanently into a video. Being able to correct a
 * word before export is the difference between a demo and something usable.
 *
 * Words are shown grouped into readable lines rather than one row per word:
 * the same line-breaking rules the .srt export uses, so what you read here is
 * what you get in the file.
 */
export const Transcript: React.FC<{
  captions: Caption[];
  playheadSec: number;
  onSeek: (seconds: number) => void;
  onEdit: (index: number, text: string) => void;
  onDelete: (index: number) => void;
  onNudge: (index: number, deltaMs: number) => void;
}> = ({ captions, playheadSec, onSeek, onEdit, onDelete, onNudge }) => {
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');

  // groupIntoLines drops the original indices, but editing needs them — walk a
  // counter alongside, which is safe because grouping preserves order and
  // includes every caption exactly once.
  const lines = useMemo(() => {
    let index = 0;
    return groupIntoLines(captions).map((line) => ({
      startMs: line[0]?.startMs ?? 0,
      words: line.map((caption) => ({ caption, index: index++ })),
    }));
  }, [captions]);

  const timeMs = playheadSec * 1000;

  const commit = (index: number) => {
    const next = draft.trim();
    const current = captions[index]?.text.trim();
    // Clearing the field deletes the word. Whisper inserts phantom captions in
    // silence, and emptying one is the most direct way to say "this isn't here".
    if (next.length === 0) onDelete(index);
    else if (next !== current) onEdit(index, next);
    setEditing(null);
  };

  return (
    <div className="transcript">
      <div className="transcript-head">
        <h2>Transcript</h2>
        <span className="mono dim">
          {captions.length} words · click a word to fix it, clear it to delete
        </span>
      </div>

      <div className="lines">
        {lines.map((line, li) => (
          <div className="line" key={`${li}-${line.startMs}`}>
            <button
              className="mono seek"
              onClick={() => onSeek(line.startMs / 1000)}
              title="Jump here"
            >
              {formatTimecode(line.startMs)}
            </button>

            <p className="line-words">
              {line.words.map(({ caption, index }) =>
                editing === index ? (
                  <span className="word-editing" key={index}>
                    <input
                      className="word-edit"
                      value={draft}
                      autoFocus
                      size={Math.max(draft.length, 2)}
                      onChange={(e) => setDraft(e.target.value)}
                      // Blur normally commits, but focus moving to the adjacent
                      // nudge/delete buttons is *not* leaving the editor — without
                      // this check the first nudge click would close it.
                      onBlur={(e) => {
                        const next = e.relatedTarget as HTMLElement | null;
                        if (next && next.closest('.word-editing')) return;
                        commit(index);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commit(index);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditing(null);
                        }
                      }}
                    />
                    <button
                      className="nudge"
                      title="100 ms earlier"
                      onClick={() => onNudge(index, -100)}
                    >
                      ◀
                    </button>
                    <button
                      className="nudge"
                      title="100 ms later"
                      onClick={() => onNudge(index, 100)}
                    >
                      ▶
                    </button>
                    <button
                      className="nudge danger"
                      title="Delete this word"
                      onClick={() => onDelete(index)}
                    >
                      ✕
                    </button>
                    <button className="nudge ok" title="Done" onClick={() => commit(index)}>
                      ✓
                    </button>
                  </span>
                ) : (
                  <button
                    key={index}
                    className={`word ${
                      timeMs >= caption.startMs && timeMs < caption.endMs ? 'now' : ''
                    }`}
                    onClick={() => {
                      setDraft(caption.text.trim());
                      setEditing(index);
                    }}
                    title={`${formatTimecode(caption.startMs)} — click to edit`}
                  >
                    {caption.text.trim()}
                  </button>
                ),
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
